import type { Components, Theme } from '@mui/material/styles';
import { osirisTokens } from './tokens';

export const baseComponents: Components<Omit<Theme, 'components'>> = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: osirisTokens.bg.page,
        color: osirisTokens.text.primary,
        scrollbarWidth: 'none',
      },
      '*': {
        scrollbarWidth: 'none',
      },
      '*::-webkit-scrollbar': {
        width: 0,
        height: 0,
        display: 'none',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          // Figma text fields sit directly on the page bg (no fill)
          backgroundColor: 'transparent',
          '& fieldset': { borderColor: osirisTokens.border.lighter },
          // error keeps its red border through hover and focus
          '&:not(.Mui-error):hover fieldset': { borderColor: osirisTokens.border.active },
          '&.Mui-focused:not(.Mui-error) fieldset': { borderColor: osirisTokens.border.active },
          '& .MuiOutlinedInput-input': {
            padding: '6px 8px 6px 12px',
            fontSize: '14px',
          },
          '&.MuiInputBase-multiline': {
            padding: 0,
          },
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        minHeight: '20px',
        margin: '2px 0 0',
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        minHeight: '54px',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        // Uniform field chrome for TextFields AND raw Selects (Figma text
        // field S): 1px brd-input-default border, #826cfc focus, radius 4
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: osirisTokens.border.lighter,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: osirisTokens.border.active,
        },
        // Design: active input outline is 1px (MUI default is 2px)
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: 1,
          borderColor: osirisTokens.border.active,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline, &.Mui-error:hover .MuiOutlinedInput-notchedOutline':
          { borderColor: osirisTokens.border.inputError },
        // Design: disabled state is the default state at 50% opacity
        '&.Mui-disabled': {
          opacity: 0.5,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: osirisTokens.border.lighter,
          },
          // keep normal text/icon colors — the 50% opacity above is the only
          // fading the design wants, MUI's extra disabled colors double it
          '& .MuiOutlinedInput-input': {
            WebkitTextFillColor: osirisTokens.text.primary,
            color: osirisTokens.text.primary,
          },
          '& .MuiSelect-icon': {
            color: osirisTokens.icons.tertiary,
          },
        },
      },
      input: {
        // MUI paints autofilled inputs with a blue inset box-shadow (#266798)
        // in dark mode; drop it and clip the browser's own autofill
        // background to the text so the field background stays untouched
        '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
          WebkitBoxShadow: 'none',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: osirisTokens.text.primary,
          caretColor: osirisTokens.text.primary,
        },
        // Design uses custom steppers (InputTextField) — hide native spinners
        '&[type=number]': { MozAppearance: 'textfield' },
        '&[type=number]::-webkit-outer-spin-button, &[type=number]::-webkit-inner-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
      },
    },
  },
  MuiSelect: {
    // Open the menu below the field instead of covering it (Figma To Do
    // 28713:31676 — dropdown list must not overlap the select)
    defaultProps: {
      MenuProps: {
        anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
        transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
      },
    },
    styleOverrides: {
      // Figma select is the 32px text-field frame (6px vertical padding)
      select: {
        padding: '6px 8px 6px 12px',
        fontSize: '14px',
        minHeight: 20,
        lineHeight: '20px',
      },
      icon: {
        color: osirisTokens.icons.tertiary,
      },
    },
  },
  // Autocomplete popup follows the same Dropdown-menu mock as MuiMenu
  MuiAutocomplete: {
    styleOverrides: {
      paper: {
        backgroundColor: osirisTokens.bg.page,
        backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.08))',
        borderRadius: '1px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3), 0 4px 8px 3px rgba(0, 0, 0, 0.15)',
      },
      listbox: {
        paddingTop: 0,
        paddingBottom: 8,
        '& .MuiAutocomplete-option': {
          minHeight: 32,
          paddingLeft: 12,
          paddingRight: 12,
          fontFamily: osirisTokens.fonts.body,
          fontSize: 14,
          color: osirisTokens.text.secondary,
          '&.Mui-focused': {
            backgroundColor: osirisTokens.accent.darker,
            color: osirisTokens.text.primary,
          },
          '&[aria-selected="true"]': {
            backgroundColor: osirisTokens.bg.surface,
            color: osirisTokens.text.primary,
          },
        },
      },
      noOptions: {
        fontFamily: osirisTokens.fonts.body,
        fontSize: 13,
        color: osirisTokens.text.tertiary,
      },
      // 32px field (Figma text field S) — Autocomplete's own paddings are
      // root-scoped per size (`& .MuiOutlinedInput-root.MuiInputBase-sizeSmall`),
      // so the overrides must repeat those exact selectors to win
      root: {
        // 12px text inset (Figma text field S) lives on the root, replacing
        // MUI's 9px; the input adds none — the doubled class outranks
        // MuiTextField's own 12px input padding
        '& .MuiOutlinedInput-root': {
          minHeight: 32,
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 12,
          '& .MuiAutocomplete-input': { paddingTop: 6, paddingBottom: 6 },
          '& .MuiAutocomplete-input.MuiOutlinedInput-input': { paddingLeft: 0 },
        },
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 12,
          '& .MuiAutocomplete-input': { paddingTop: 6, paddingBottom: 6 },
        },
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        // page bg + 8% white overlay per Dropdown-menu mock
        backgroundColor: osirisTokens.bg.page,
        backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.08))',
        borderRadius: '1px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3), 0 4px 8px 3px rgba(0, 0, 0, 0.15)',
      },
      list: {
        paddingTop: 0,
        paddingBottom: 8,
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        minHeight: 32,
        paddingLeft: 12,
        paddingRight: 12,
        fontFamily: osirisTokens.fonts.body,
        fontSize: 14,
        color: osirisTokens.text.secondary,
        borderRadius: '1px',
        '&:hover': {
          backgroundColor: osirisTokens.accent.darker,
          color: osirisTokens.text.primary,
        },
        '&.Mui-selected': {
          backgroundColor: osirisTokens.bg.surface,
          color: osirisTokens.text.primary,
          '&:hover': {
            backgroundColor: osirisTokens.accent.darker,
            color: osirisTokens.text.primary,
          },
          '&.Mui-focusVisible': {
            backgroundColor: osirisTokens.bg.surface,
          },
        },
        // size S per mock
        '&.MuiMenuItem-dense': {
          minHeight: 24,
          paddingLeft: 8,
          paddingRight: 8,
          fontSize: 12,
        },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      // 32px fields across the app (Figma text field S)
      input: {
        padding: '6px 8px 6px 12px',
        fontSize: '14px',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none' as const,
        boxShadow: 'none',
        // Figma buttons are 2px-radius across all sizes/roles
        borderRadius: '2px',
        fontFamily: osirisTokens.fonts.body,
        '&:hover': { boxShadow: 'none' },
        '&:active': { boxShadow: 'none' },
        '&:focus': { boxShadow: 'none' },
        '&.Mui-disabled': { boxShadow: 'none' },
      },
      outlined: {
        // Figma Tertiary/Cancel buttons: alpha/dark/500 outline
        borderColor: osirisTokens.alpha.dark500,
        color: osirisTokens.text.secondary,
        '&:hover': {
          borderColor: osirisTokens.border.btnMap,
          backgroundColor: osirisTokens.bg.surface,
        },
      },
      // Figma Size-S buttons: 32px tall, IBM Plex Sans 13 medium
      sizeSmall: {
        height: 32,
        padding: '0 16px',
        fontSize: 13,
        fontWeight: 500,
        letterSpacing: '0.01em',
      },
      // Figma Size-M buttons: 40px tall, IBM Plex Sans 14 medium
      sizeMedium: {
        minHeight: 40,
        padding: '0 16px',
        fontSize: 14,
        fontWeight: 500,
        letterSpacing: '0.01em',
      },
      text: {
        color: osirisTokens.text.primary,
        '&:hover': { backgroundColor: osirisTokens.bg.surface },
      },
    },
    // MUI 9 dropped composite styleOverrides keys (containedPrimary etc.)
    variants: [
      {
        props: { variant: 'contained' as const, color: 'primary' as const },
        style: {
          backgroundColor: osirisTokens.accent.main,
          color: '#ffffff',
          // Figma: Primary-Active darkens to bg-btn-primary-active
          '&:hover': { backgroundColor: osirisTokens.accent.dark },
        },
      },
      // Figma Secondary button: purple outline + purple label
      {
        props: { variant: 'outlined' as const, color: 'secondary' as const },
        style: {
          borderColor: osirisTokens.border.btnSecondary,
          color: osirisTokens.text.active,
          '&:hover': {
            borderColor: osirisTokens.border.btnSecondary,
            backgroundColor: osirisTokens.bg.surface,
          },
        },
      },
    ],
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: '2px',
        color: osirisTokens.icons.primary,
        '&:hover': { backgroundColor: osirisTokens.bg.surface },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: osirisTokens.bg.page,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: osirisTokens.bg.surface,
        border: `1px solid ${osirisTokens.border.primary}`,
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: osirisTokens.bg.page,
        backgroundImage: 'none',
        borderBottom: `1px solid ${osirisTokens.border.primary}`,
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: osirisTokens.border.primary,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      root: {
        '& .MuiDrawer-paper': {
          backgroundColor: osirisTokens.bg.page,
          borderColor: osirisTokens.border.primary,
          backgroundImage: 'none',
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      root: {
        // Lighter scrim than MUI's default rgba(0,0,0,0.5) so the page stays
        // visible behind modals, matching the Figma backdrop.
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(26, 26, 26, 0.95)',
        },
      },
      paper: {
        // Figma modal (28669:17712): page bg + 3% white overlay, dark/130
        // border — the overlay gradient also disables MUI's elevation tint
        borderRadius: '2px',
        backgroundColor: osirisTokens.bg.page,
        backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))',
        border: `1px solid ${osirisTokens.alpha.dark130}`,
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.5)',
      },
    },
  },
  // Figma modal header: 40px section-header strip, Tektur 13 caps
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        display: 'flex',
        alignItems: 'center',
        minHeight: 40,
        padding: '0 16px',
        backgroundColor: osirisTokens.bg.header,
        fontFamily: osirisTokens.fonts.display,
        fontSize: 13,
        fontWeight: 400,
        letterSpacing: '0.08em',
        textTransform: 'uppercase' as const,
        color: osirisTokens.text.primary,
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '16px 40px 24px',
        '.MuiDialogTitle-root + &': { paddingTop: 16 },
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '0 16px 16px',
        gap: 16,
        '& > :not(style) ~ :not(style)': { marginLeft: 0 },
        // Figma modal actions (28669:17712): 32px-tall buttons — minHeight too,
        // else MuiButton sizeMedium's minHeight: 40 wins
        '& .MuiButton-root': { height: 32, minHeight: 32 },
      },
    },
  },
  // Figma Callout (28400:20699): ad-hoc <Alert> hints inherit the Callout's
  // geometry and mono type so inline hints read consistently app-wide
  MuiAlert: {
    styleOverrides: {
      root: {
        alignItems: 'center',
        minHeight: 40,
        padding: '12px 16px',
        borderRadius: '2px',
        backgroundColor: osirisTokens.bg.secondary,
        color: osirisTokens.text.secondary,
        fontFamily: osirisTokens.fonts.mono,
        fontSize: 11,
        letterSpacing: '0.02em',
        lineHeight: 1.5,
      },
      icon: {
        padding: 0,
        marginRight: 12,
        opacity: 1,
        '& .MuiSvgIcon-root': { fontSize: 20 },
      },
      message: { padding: 0 },
      action: { padding: 0, marginRight: 0, alignItems: 'center' },
    },
    variants: [
      {
        props: { severity: 'error' as const },
        style: {
          backgroundColor: 'rgba(250, 121, 101, 0.15)',
          color: osirisTokens.text.error,
          '& .MuiAlert-icon': { color: osirisTokens.icons.error },
        },
      },
      {
        props: { severity: 'warning' as const },
        style: {
          backgroundColor: 'rgba(255, 204, 0, 0.15)',
          color: osirisTokens.text.warning,
          '& .MuiAlert-icon': { color: osirisTokens.icons.warning },
        },
      },
      {
        props: { severity: 'success' as const },
        style: {
          backgroundColor: 'rgba(116, 200, 124, 0.15)',
          color: osirisTokens.text.success,
          '& .MuiAlert-icon': { color: osirisTokens.icons.success },
        },
      },
      {
        props: { severity: 'info' as const },
        style: {
          backgroundColor: 'rgba(32, 166, 241, 0.15)',
          color: osirisTokens.text.secondary,
          '& .MuiAlert-icon': { color: osirisTokens.icons.info },
        },
      },
    ],
  },
  MuiTooltip: {
    styleOverrides: {
      // Light tooltip over the dark UI (Figma: Bg/bg-tooltip + invert text)
      tooltip: {
        backgroundColor: osirisTokens.bg.tooltip,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: `1px solid ${osirisTokens.alpha.light100}`,
        borderRadius: '2px',
        fontFamily: osirisTokens.fonts.body,
        fontSize: '12px',
        fontWeight: 500,
        letterSpacing: '0.01em',
        color: osirisTokens.text.invertPrimary,
      },
    },
    // No caret on tooltips (Figma To Do 28693:28254)
    defaultProps: { arrow: false },
  },
  // Figma toggle component 28376:16451 — inset thumb, accent track when on
  // (Figma To Do 28742:23763)
  MuiSwitch: {
    styleOverrides: {
      root: {
        width: 36,
        height: 20,
        padding: 0,
        // size="small" keeps the same Figma geometry — MUI's sizeSmall variant
        // uses higher-specificity descendant selectors that would otherwise
        // re-inflate the thumb/padding over this 36×20 track
        '&.MuiSwitch-sizeSmall': {
          width: 36,
          height: 20,
          padding: 0,
          '& .MuiSwitch-thumb': { width: 14, height: 14 },
          '& .MuiSwitch-switchBase': {
            padding: 3,
            '&.Mui-checked': { transform: 'translateX(16px)' },
          },
        },
      },
      switchBase: {
        padding: 3,
        // Figma D UPD: green so the active state stands out among purple accents
        '&.Mui-checked': {
          transform: 'translateX(16px)',
          '& .MuiSwitch-thumb': { backgroundColor: osirisTokens.icons.primary },
          '& + .MuiSwitch-track': { backgroundColor: osirisTokens.status.successDark, opacity: 1 },
        },
      },
      thumb: {
        width: 14,
        height: 14,
        boxShadow: 'none',
        backgroundColor: osirisTokens.text.primary,
      },
      track: {
        borderRadius: 10,
        backgroundColor: osirisTokens.alpha.dark300,
        opacity: 1,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderColor: osirisTokens.border.primary,
      },
      head: {
        fontFamily: osirisTokens.fonts.mono,
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: osirisTokens.text.tertiary,
        backgroundColor: osirisTokens.bg.surface,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      // Figma section-header tabs: IBM Plex Sans 13, no caps, 2px underline
      root: {
        textTransform: 'none',
        fontFamily: osirisTokens.fonts.body,
        fontSize: '13px',
        letterSpacing: '0.02em',
        color: osirisTokens.text.tertiary,
        '&.Mui-selected': { color: osirisTokens.text.primary },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        // Figma: active tab underline is brd-input-active (#826cfc)
        backgroundColor: osirisTokens.border.active,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      // Figma chip: 24px tall, mono 12 caps, 1px radius
      root: {
        borderRadius: '1px',
        fontFamily: osirisTokens.fonts.mono,
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '0.02em',
        height: 24,
      },
      // Tags inside 32px selects/autocomplete are 20px (Figma tag-01)
      sizeSmall: {
        height: 20,
      },
    },
  },
};
